/**
 * URL encode a locator.
 *
 * Use this for **locators only** (project locators, revision locators, package strings).
 * Locators reach us in a mix of encoded and decoded states depending on where they came
 * from, so `nuc` decodes first to normalize them, then encodes once. That decode pass is
 * what makes it unsafe for anything else.
 *
 * Do NOT use it on arbitrary user-supplied strings, in particular branch and tag names
 * (use `encodeReference` instead), revision messages, project titles, or search terms.
 * Any value holding a literal percent escape survives the encode but not the round trip.
 * A branch genuinely named `release%20candidate` encodes to a segment that still reads
 * `release%20candidate`, so the URL looks correct, but the router decodes it once more
 * and the page gets `release candidate` back. The name no longer matches the row in
 * `References` and the branch reads as missing.
 *
 * Rule of thumb: if the value is compared for equality against a database column after
 * the round trip, `nuc` is the wrong tool.
 */
export default function nuc(comp: string, maxDepth: number = 2) {
  if (!comp) {
    return '';
  }
  const originalStr = `${comp}`;
  let prev = null;
  let cur = originalStr;

  let innerMaxDepth = maxDepth;

  /**
   * The purpose of this loop is to handle the case where the string has been URI encoded multiple times.
   * It will try and decode the string until either:
   * 1. The decoded string is the same as the previous string (which means decoding did nothing)
   * 2. A `+` character is found (which means we've fully decoded a locator string)
   * 3. We've reached the maximum depth and won't attempt to decode any more.
   *
   * This function is apparently primarily meant for encoding **Locator** strings, even though we use it for much more.
   */
  while (prev !== cur && !cur.includes('+') && innerMaxDepth-- > 0) {
    prev = cur;
    try {
      cur = decodeURIComponent(cur);
    } catch (e) {
      // If the string is not valid URI encoded, return the encoded version of the string.
      // For example, if the string is not URI encoded and contains a plaintext `%`, we don't want to fail but we do
      // want to return the encoded version of the string.
      return encodeURIComponent(cur);
    }
  }
  return encodeURIComponent(cur);
}
